import React, { useEffect, useRef, useState } from "react";
import { ButtonGroup, Dropdown, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiFilterDropdown from "../../components/MultiFilterDropdown/MultiFilterDropdown";
import { useSession } from "../../contexts/SessionContext";
import { LanguageEnum } from "../../models/Language";
import ApiService from "../../services/ApiService";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly, formatDatePickerValue, getLocalHourRange, normalizeDate } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import { Animator, Author, Meeting, MeetingCurrentFilters, MeetingTypes, Program } from "./Meetings.d";
import {
  MEETINGS_FILTERS_STORAGE_KEY,
  computeDay,
  downloadMeetingsCsv,
  fetchMeetingTypes,
  fetchResources,
  getStoredMeetingsFilters,
} from "./Meetings.function";
import "./Meetings.scss";
import CreateCustomFilterPopup from "./components/CreateCustomFilterPopup/CreateCustomFilterPopup";
import MeetingCreationModal from "./components/CreationModal/CreationModal";
import CustomFilterDropdown from "./components/CustomFilterDropdown/CustomFilterDropdown";
import MeetingsCalendar from "./components/MeetingsCalendar/MeetingsCalendar";

const Meetings = () => {
  const { language } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSearchParams = Array.from(searchParams.keys()).length > 0;
  const [loading, setLoading] = useState({ staff: true, types: true });
  const [tempStartDate, setTempStartDate] = useState<string | null>(computeDay());
  const [tempEndDate, setTempEndDate] = useState<string | null>(computeDay(7));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [storedFilters] = useState(getStoredMeetingsFilters);

  const [parameters, setParameters] = useState<any>({
    authors: storedFilters.authors,
    animators: storedFilters.animators,
    programs: storedFilters.programs,
    meetingTypes: storedFilters.meetingTypes,
    languages: storedFilters.languages,
    startDate: storedFilters.startDate,
    endDate: storedFilters.endDate,
    showUnassigned: storedFilters.showUnassigned,
    showDetailedSearch: storedFilters.showDetailedSearch,
  });

  const [authors, setAuthors] = useState<Author[]>([]);
  const [animators, setAnimators] = useState<Animator[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingTypes[]>([]);
  const availableLanguages = Object.values(LanguageEnum).map((lang) => ({ value: lang, display: lang }));
  const [showModalCreation, setShowModalCreation] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const resetTriggered = useRef(false);
  const [dateError, setDateError] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>(storedFilters.search);
  const [showPopupCreation, setShowPopupCreation] = useState(false);
  const [showPopupSelection, setShowPopupSelection] = useState(false);
  const [activeCustomFilter, setActiveCustomFilter] = useState(storedFilters.activeCustomFilter);
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const latestRequest = useRef(0);

  const activeFilterCount = [
    parameters.meetingTypes.length > 0,
    parameters.authors.length > 0,
    parameters.animators.length > 0,
    parameters.programs.length > 0,
    parameters.showUnassigned,
    parameters.startDate || parameters.endDate,
  ].filter(Boolean).length;

  useEffect(() => {
    if (hasSearchParams) return;

    let requestId = Date.now();
    setCalendarLoading(true);
    ApiService.get(_generateRemoteUrl())
      .then((response) => {
        if (requestId !== latestRequest.current) return;
        setMeetings(response.data.meetings);
      })
      .catch((error) => console.error("Failed to fetch meetings", error))
      .finally(() => {
        if (requestId === latestRequest.current) setCalendarLoading(false);
      });
    latestRequest.current = requestId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewMode,
    parameters.startDate,
    parameters.endDate,
    parameters.authors,
    parameters.animators,
    parameters.programs,
    parameters.languages,
    parameters.meetingTypes,
    parameters.showUnassigned,
    searchQuery,
    hasSearchParams,
  ]);

  useEffect(() => {
    fetchResources(setAuthors, setAnimators, setPrograms, setLoading, language);
    fetchMeetingTypes(setMeetingTypes, setLoading, language);
  }, [language]);

  const _generateRemoteUrl = (downloadUrl = false, downloadWithTimezone = false) => {
    const baseUrl = downloadUrl
      ? `meetings/download?hours_offset=${downloadWithTimezone ? new Date().getTimezoneOffset() / -60 : "0"}`
      : "meetings?get_animators_info=true&get_authors_info=true&get_meeting_type_info=true&fetch_users_count=true&get_campus_names=true&get_cohort_infos=true";
    const filters = [];
    if (parameters.startDate) filters.push(`start_date=${new Date(parameters.startDate).toISOString()}`);
    if (parameters.endDate) {
      const end = new Date(parameters.endDate);
      end.setHours(23, 59, 59, 999);
      filters.push(`end_date=${end.toISOString()}`);
    }
    if (parameters.meetingTypes.length > 0) filters.push(`type_ids=${parameters.meetingTypes.join(",")}`);
    if (parameters.authors.length > 0) filters.push(`author_ids=${parameters.authors.filter(Boolean).join(",")}`);
    if (parameters.animators.length > 0) filters.push(`animator_ids=${parameters.animators.filter(Boolean).join(",")}`);
    if (parameters.programs.length > 0) filters.push(`program=${parameters.programs.filter(Boolean).join(",")}`);
    if (parameters.languages.length > 0) filters.push(`language=${parameters.languages.join(",")}`);
    if (parameters.showUnassigned) filters.push("show_unassigned=true");
    if (searchQuery) filters.push(`search=${searchQuery.toLowerCase()}`);
    return `${baseUrl}${filters.length > 0 ? `&${filters.join("&")}` : ""}`;
  };

  useEffect(() => {
    if (!Array.from(searchParams.keys()).length) return;

    const authorsFromUrl = searchParams.get("authors")?.split(",") || [];
    const animatorsFromUrl = searchParams.get("animators")?.split(",") || [];
    const isAuthorOnly = authorsFromUrl.length > 0 && animatorsFromUrl.length === 0;
    const isAnimatorOnly = animatorsFromUrl.length > 0 && authorsFromUrl.length === 0;

    if ((isAuthorOnly || isAnimatorOnly) && searchParams.get("reset") === "true") {
      resetTriggered.current = true;
      const defaultStart = computeDay();
      const defaultEnd = computeDay(searchParams.get("period") ? Number(searchParams.get("period")) : 7);
      setParameters((parameters: any) => ({
        ...parameters,
        authors: isAuthorOnly ? authorsFromUrl : [],
        animators: isAnimatorOnly ? animatorsFromUrl : [],
        programs: [],
        meetingTypes: [],
        languages: [],
        startDate: defaultStart,
        endDate: defaultEnd,
        showUnassigned: false,
      }));
      setActiveCustomFilter("");
      setTempStartDate(defaultStart);
      setTempEndDate(defaultEnd);
      setSearchParams({}, { replace: true });
    } else {
      setParameters((parameters: any) => ({
        ...parameters,
        authors: authorsFromUrl,
        animators: animatorsFromUrl,
        programs: searchParams.get("programs")?.split(",") || [],
        meetingTypes: searchParams.get("meeting_types")?.split(",") || [],
        languages: searchParams.get("languages")?.split(",") || [],
        startDate: searchParams.get("start_date") || computeDay(),
        endDate: searchParams.get("end_date") || computeDay(7),
        showUnassigned: searchParams.get("show_unassigned") === "true",
      }));
      setTempStartDate(searchParams.get("start_date") || computeDay());
      setTempEndDate(searchParams.get("end_date") || computeDay(7));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    localStorage.setItem(
      MEETINGS_FILTERS_STORAGE_KEY,
      JSON.stringify({
        authors: parameters.authors,
        animators: parameters.animators,
        programs: parameters.programs,
        meetingTypes: parameters.meetingTypes,
        languages: parameters.languages,
        showUnassigned: parameters.showUnassigned,
        showDetailedSearch: parameters.showDetailedSearch,
        search: searchQuery,
        activeCustomFilter,
      })
    );
  }, [
    parameters.authors,
    parameters.animators,
    parameters.programs,
    parameters.meetingTypes,
    parameters.languages,
    parameters.showUnassigned,
    parameters.showDetailedSearch,
    searchQuery,
    activeCustomFilter,
  ]);

  const getColumns = () => {
    const baseColumns = [
      {
        key: "dt_start",
        name: translate(language, "GLOBAL.START_DATE"),
        render: (item: Meeting) => formatDateHumanly(language, item.dt_start),
        sortable: true,
      },
      {
        key: "hours_range",
        name: translate(language, "PAGES.MEETINGS.TABLE_HEADER.TIME"),
        render: (item: Meeting) => getLocalHourRange(item.hours_range, item.dt_start),
      },
      {
        key: "name",
        name: translate(language, "PAGES.MEETINGS.TABLE_HEADER.MEETING_NAME"),
        sortable: true,
        render: (item: Meeting) => item.name,
      },
      {
        key: "type_name",
        name: translate(language, "PAGES.MEETINGS.TABLE_HEADER.MEETING_TYPE"),
        render: (item: Meeting) => item.type.name,
      },
      {
        key: "author",
        name: translate(language, "GLOBAL.ORGANIZER"),
        render: (item: Meeting) => (
          <span
            className="text-primary clickable"
            onClick={(event) => {
              event.stopPropagation();
              completeNavigate(event, navigate, `/user/${item.author.id}`);
            }}
          >
            {item.author.full_name}
          </span>
        ),
      },
      {
        key: "animators",
        name: translate(language, "GLOBAL.ANIMATORS"),
        render: (item: Meeting) => (
          <div>
            {item.animators?.length ? (
              item.animators.map((animator, index) => (
                <div key={animator.id}>
                  <span
                    className="text-primary clickable"
                    onClick={(event) => {
                      event.stopPropagation();
                      completeNavigate(event, navigate, `/user/${animator.id}`);
                    }}
                  >
                    {animator.full_name}
                  </span>
                  {index < item.animators!.length - 1 && ", "}
                </div>
              ))
            ) : (
              <span className="text-muted">{translate(language, "PAGES.MEETINGS.TABLE_BODY.NO_ANIMATORS")}</span>
            )}
          </div>
        ),
      },
      {
        key: "cohorts",
        name: translate(language, "PAGES.MEETINGS.TABLE_HEADER.COHORTS"),
        render: (item: Meeting) => {
          if (item.cohort_count === 0) return <span className="text-muted">-</span>;
          return (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip aria-label="cohorts-list">
                  {item.cohorts?.map((cohort) => (
                    <div key={cohort.id}>
                      {cohort.name} (#{cohort.id})
                    </div>
                  ))}
                </Tooltip>
              }
            >
              <span className="text-primary clickable">
                {item.cohort_count}{" "}
                {translate(
                  language,
                  item.cohort_count === 1 ? "PAGES.MEETINGS.TABLE_BODY.COHORT" : "PAGES.MEETINGS.TABLE_BODY.COHORTS"
                )}
              </span>
            </OverlayTrigger>
          );
        },
      },
    ];

    if (parameters.showDetailedSearch) {
      const detailedColumns = [
        {
          key: "dt_created",
          name: translate(language, "GLOBAL.CREATED_DATE"),
          sortable: true,
          render: (item: Meeting) => formatDateHumanly(language, item.dt_created),
        },
        {
          key: "dt_updated",
          name: translate(language, "GLOBAL.UPDATED_DATE"),
          sortable: true,
          render: (item: Meeting) =>
            item.dt_updated ? formatDateHumanly(language, item.dt_updated) : <span className="text-muted">N/A</span>,
        },
        {
          key: "campus_names",
          name: translate(language, "PAGES.MEETINGS.TABLE_HEADER.CAMPUS"),
          render: (item: Meeting) =>
            item.campus_names && item.campus_names.length > 0 ? (
              item.campus_names.join(", ")
            ) : (
              <span className="text-muted">{translate(language, "PAGES.MEETINGS.TABLE_BODY.NO_CAMPUS")}</span>
            ),
        },
      ];
      return [...baseColumns, ...detailedColumns];
    }

    return baseColumns;
  };

  const currentFilters = {
    authors: parameters.authors,
    animators: parameters.animators,
    programs: parameters.programs,
    meetingTypes: parameters.meetingTypes,
    languages: parameters.languages,
  };

  const _handleApplyCustomFilter = (filters: MeetingCurrentFilters, filterName: string) => {
    setParameters((parameters: any) => ({
      ...parameters,
      authors: filters.authors ?? [],
      animators: filters.animators ?? [],
      programs: filters.programs ?? [],
      meetingTypes: filters.meetingTypes ?? [],
      languages: filters.languages ?? [],
    }));
    setActiveCustomFilter(filterName);
  };

  const hasActiveFilters =
    parameters.meetingTypes.length > 0 ||
    parameters.authors.length > 0 ||
    parameters.animators.length > 0 ||
    parameters.programs.length > 0 ||
    parameters.languages.length > 0;

  const _clearAllFilters = () => {
    setParameters((parameters: any) => ({
      ...parameters,
      meetingTypes: [],
      authors: [],
      animators: [],
      programs: [],
      languages: [],
      showDetailedSearch: false,
    }));
    setActiveCustomFilter("");
  };

  const ActiveFilterChips = () => (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      {(parameters.startDate || parameters.endDate) && (
        <span className="badge bg-light text-dark border clickable" onClick={() => setDrawerOpen(true)}>
          {parameters.startDate && parameters.endDate
            ? `${formatDatePickerValue(parameters.startDate)} → ${formatDatePickerValue(parameters.endDate)}`
            : parameters.startDate
            ? `${translate(language, "GLOBAL.START_DATE")} : ${formatDatePickerValue(parameters.startDate)}`
            : `${translate(language, "GLOBAL.END_DATE")} : ${formatDatePickerValue(parameters.endDate)}`}
        </span>
      )}
      {activeCustomFilter && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {activeCustomFilter}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              _clearAllFilters();
            }}
          >
            ✕
          </span>
        </span>
      )}
      {parameters.meetingTypes.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.MEETINGS.FILTERS.TYPE_COUNT").replace(
            "{{count}}",
            `${parameters.meetingTypes.length}`
          )}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setParameters((parameters: any) => ({ ...parameters, meetingTypes: [] }));
              setActiveCustomFilter("");
            }}
          >
            ✕
          </span>
        </span>
      )}
      {parameters.authors.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.MEETINGS.FILTERS.AUTHOR_COUNT").replace(
            "{{count}}",
            `${parameters.authors.length}`
          )}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setParameters((parameters: any) => ({ ...parameters, authors: [] }));
              setActiveCustomFilter("");
            }}
          >
            ✕
          </span>
        </span>
      )}
      {parameters.animators.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.MEETINGS.FILTERS.ANIMATOR_COUNT").replace(
            "{{count}}",
            `${parameters.animators.length}`
          )}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setParameters((parameters: any) => ({ ...parameters, animators: [] }));
              setActiveCustomFilter("");
            }}
          >
            ✕
          </span>
        </span>
      )}
      {parameters.programs.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.MEETINGS.FILTERS.PROGRAM_COUNT").replace(
            "{{count}}",
            `${parameters.programs.length}`
          )}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setParameters((parameters: any) => ({ ...parameters, programs: [] }));
              setActiveCustomFilter("");
            }}
          >
            ✕
          </span>
        </span>
      )}
      {parameters.languages.length > 0 && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.MEETINGS.FILTERS.LANGUAGE_COUNT").replace(
            "{{count}}",
            `${parameters.languages.length}`
          )}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setParameters((parameters: any) => ({ ...parameters, languages: [] }));
              setActiveCustomFilter("");
            }}
          >
            ✕
          </span>
        </span>
      )}
      {parameters.showUnassigned && (
        <span
          className="badge bg-primary-subtle text-primary border border-primary-subtle"
          onClick={() => setDrawerOpen(true)}
        >
          {translate(language, "PAGES.MEETINGS.FILTERS.UNASSIGNED_MEETINGS")}
          <span
            className="ms-1 clickable"
            onClick={(event) => {
              event.stopPropagation();
              setParameters((parameters: any) => ({ ...parameters, showUnassigned: false }));
              setActiveCustomFilter("");
            }}
          >
            ✕
          </span>
        </span>
      )}
      {hasActiveFilters && (
        <span className="clickable dark-gray-font text-decoration-underline xs-regular" onClick={_clearAllFilters}>
          {translate(language, "PAGES.MEETINGS.FILTERS.CLEAR_FILTERS")}
        </span>
      )}
    </div>
  );

  return (
    <div id="meetings" className="d-flex flex-column" style={{ minHeight: "100vh" }}>
      <div className="d-flex align-items-center gap-2 p-3 white-background border-bottom flex-wrap">
        <h1 className="mb-0 h2 me-1">{translate(language, "PAGES.MEETINGS.TITLE")}</h1>

        <ListerSearch setSearchQuery={setSearchQuery} timeDelay={500} showSearchIcon />

        <DstButton
          btnClass="d-flex align-items-center"
          btnImageBefore={
            <Form.Check
              type="switch"
              className="check-box s-bold me-2"
              id="detailed-search-switch"
              checked={parameters.showDetailedSearch}
            />
          }
          value={translate(language, "PAGES.MEETINGS.FILTERS.DETAILED_SEARCH")}
          variant="light"
          clickFunction={() =>
            setParameters((parameters: any) => ({ ...parameters, showDetailedSearch: !parameters.showDetailedSearch }))
          }
        />
        <div className="position-relative">
          <DstButton
            variant={activeFilterCount > 0 ? "primary" : "light"}
            btnImageBefore={<span className="dst-icon-filter-lines me-2" />}
            value={translate(language, "PAGES.MEETINGS.FILTERS.FILTERS")}
            clickFunction={() => setDrawerOpen(true)}
          />
          {activeFilterCount > 0 && (
            <span
              className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
              style={{ fontSize: 10 }}
            >
              {activeFilterCount}
            </span>
          )}
        </div>
        <div className="ms-auto d-flex gap-2 flex-wrap">
          <DstButton
            variant="light"
            btnImageBefore={<span className="dst-icon-calendar me-2" />}
            value={
              viewMode === "table"
                ? translate(language, "PAGES.MEETINGS.VIEW_MODE.CALENDAR")
                : translate(language, "PAGES.MEETINGS.VIEW_MODE.TABLE")
            }
            clickFunction={() => setViewMode((viewMode) => (viewMode === "table" ? "calendar" : "table"))}
          />
          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings/batch" }]}>
            <DstButton
              variant="light"
              value={translate(language, "PAGES.MEETINGS.BUTTONS.IMPORT_CSV")}
              btnImageBefore={<span className="me-2 dst-icon-download-03" />}
              clickFunction={() => navigate("/meetings/import")}
            />
          </PermissionComponent>
          <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/meetings/download" }]}>
            <Dropdown as={ButtonGroup}>
              <DstButton
                variant="light"
                value={translate(language, "PAGES.MEETINGS.BUTTONS.SAVE.TITLE")}
                btnImageBefore={<span className="me-2 dst-icon-download-01" />}
                clickFunction={() => downloadMeetingsCsv(language, _generateRemoteUrl(true), setDownloading)}
                loading={downloading}
              />
              <Dropdown.Toggle split variant="light" id="dropdown-split-basic" />
              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() => downloadMeetingsCsv(language, _generateRemoteUrl(true, true), setDownloading)}
                >
                  {translate(language, "PAGES.MEETINGS.BUTTONS.SAVE.LOCAL")}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </PermissionComponent>
          <PermissionComponent neededPermission={[{ methods: ["POST"], route: "/meetings" }]}>
            <DstButton
              value={translate(language, "PAGES.MEETINGS.BUTTONS.CREATE_MEETING")}
              btnImageBefore={<span className="me-2 dst-icon-plus" />}
              clickFunction={() => setShowModalCreation(true)}
            />
            <MeetingCreationModal
              language={language}
              setShowCreationModal={setShowModalCreation}
              showCreationModal={showModalCreation}
            />
          </PermissionComponent>
        </div>
      </div>
      <div className="p-3 flex-grow-1 overflow-auto">
        <ActiveFilterChips />
        {!hasSearchParams &&
          (viewMode === "table" ? (
            <DstLiveTable
              remote={_generateRemoteUrl()}
              remoteKey="meetings"
              columns={getColumns()}
              onRowClick={(event, item) => completeNavigate(event, navigate, `/meeting/${item.id}`)}
              pagination
              itemsPerPage={20}
              paginationResizeOptions={[20, 50, 100]}
              paginationAlwaysShowFooter
              defaultSortKey="dt_start"
              defaultSortOrder="asc"
            />
          ) : (
            <MeetingsCalendar
              meetings={meetings}
              onEventClick={(id: string) => navigate(`/meeting/${id}`)}
              onDatesChange={(start: Date, end: Date) => {
                const newStart = normalizeDate(start.toLocaleDateString("fr-FR"));
                const adjustedEnd = new Date(end.getTime() - 1);
                const newEnd = normalizeDate(adjustedEnd.toLocaleDateString("fr-FR"));
                setParameters((prev: any) => {
                  if (prev.startDate === newStart && prev.endDate === newEnd) return prev;
                  return { ...prev, startDate: newStart, endDate: newEnd };
                });
                setTempStartDate(newStart);
                setTempEndDate(newEnd);
              }}
              loading={calendarLoading}
            />
          ))}
      </div>
      {drawerOpen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 overlay-backdrop"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div
        className={`drawer-right position-fixed top-0 end-0 h-100 white-background d-flex flex-column ${
          drawerOpen ? "is-open" : ""
        }`}
      >
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
          <span className="s-bold h5 mb-0">{translate(language, "PAGES.MEETINGS.FILTERS.FILTERS")}</span>
          <DstButton
            variant="light"
            value=""
            btnImageBefore={<span className="dst-icon-x-close" />}
            clickFunction={() => setDrawerOpen(false)}
          />
        </div>
        <div className="p-3 d-flex flex-column gap-3 overflow-auto flex-grow-1">
          <div className="d-flex flex-column gap-2">
            <span className="xs-regular dark-gray-font text-uppercase d-flex">
              {translate(language, "PAGES.MEETINGS.FILTERS.PERIOD")}
            </span>
            <div>
              <label htmlFor="start-date" className="form-label dark-gray-font xs-regular mb-1">
                {translate(language, "GLOBAL.START_DATE")}
              </label>
              <input
                type="date"
                id="start-date"
                className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
                value={tempStartDate || ""}
                onChange={(event) => setTempStartDate(event.target.value)}
              />
            </div>
            <div>
              <label htmlFor="end-date" className="form-label dark-gray-font xs-regular mb-1">
                {translate(language, "GLOBAL.END_DATE")}
              </label>
              <input
                type="date"
                id="end-date"
                className={`form-control s-regular ${dateError ? "is-invalid" : ""}`}
                value={tempEndDate || ""}
                onChange={(event) => setTempEndDate(event.target.value)}
              />
            </div>
            <div className="d-flex gap-2">
              <DstButton
                clickFunction={() => {
                  if (tempStartDate && tempEndDate && new Date(tempEndDate) < new Date(tempStartDate)) {
                    setDateError(true);
                    return;
                  }
                  setDateError(false);
                  setParameters((parameters: any) => ({
                    ...parameters,
                    startDate: tempStartDate,
                    endDate: tempEndDate,
                  }));
                  setActiveCustomFilter("");
                }}
                value={translate(language, "PAGES.MEETINGS.BUTTONS.APPLY_DATES")}
                btnImageBefore={<span className="dst-icon-search-md me-1" />}
                btnClass="flex-grow-1"
              />

              <DstButton
                clickFunction={() => {
                  setTempStartDate(null);
                  setTempEndDate(null);
                  setParameters((parameters: any) => ({ ...parameters, startDate: null, endDate: null }));
                  setActiveCustomFilter("");
                }}
                value=""
                btnImageBefore={<span className="dst-icon-eraser" />}
                variant="secondary"
                tooltip={translate(language, "PAGES.MEETINGS.FILTERS.CLEAR_DATES")}
              />
            </div>
          </div>

          <hr className="my-0" />

          <div className="d-flex flex-column gap-2">
            <span className="xs-regular dark-gray-font text-uppercase">
              {translate(language, "PAGES.MEETINGS.FILTERS.FILTERS")}
            </span>
            <MultiFilterDropdown
              inputTitle={translate(language, "PAGES.MEETINGS.FILTERS.MEETING_TYPE")}
              tags={meetingTypes.map((meetingType) => ({ value: meetingType.id, display: meetingType.name }))}
              onSelectTags={(tags) => {
                const tagsAsString = tags.map(String);
                setParameters((parameters: any) => ({ ...parameters, meetingTypes: tagsAsString }));
                if (
                  tagsAsString.length !== parameters.meetingTypes.length ||
                  tagsAsString.some((tag, idx) => tag !== parameters.meetingTypes[idx])
                )
                  setActiveCustomFilter("");
              }}
              defaultValue={parameters.meetingTypes}
              enableSelectAll
              considerEmptyOnAllSelected
              showSearchBar
              language={language}
              mock={loading.types}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              inputTitle={translate(language, "GLOBAL.ORGANIZER")}
              tags={authors.map((author) => ({ value: author.id, display: author.full_name }))}
              onSelectTags={(tags) => {
                setParameters((parameters: any) => ({ ...parameters, authors: tags.map(Number) }));
                setActiveCustomFilter("");
              }}
              defaultValue={parameters.authors}
              enableSelectAll
              considerEmptyOnAllSelected
              showSearchBar
              language={language}
              mock={loading.staff}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              inputTitle={translate(language, "GLOBAL.ANIMATOR")}
              tags={animators.map((animator) => ({ value: animator.staff_member_id, display: animator.full_name }))}
              onSelectTags={(tags) => {
                setParameters((parameters: any) => ({ ...parameters, animators: tags.map(Number) }));
                setActiveCustomFilter("");
              }}
              defaultValue={parameters.animators}
              enableSelectAll
              considerEmptyOnAllSelected
              showSearchBar
              language={language}
              mock={loading.staff}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              inputTitle={translate(language, "PAGES.MEETINGS.FILTERS.PROGRAM")}
              tags={programs.map((program) => ({ value: program.id, display: program.name }))}
              onSelectTags={(tags) => {
                setParameters((parameters: any) => ({ ...parameters, programs: tags.map(Number) }));
                setActiveCustomFilter("");
              }}
              defaultValue={parameters.programs}
              enableSelectAll
              considerEmptyOnAllSelected
              showSearchBar
              language={language}
              mock={loading.staff}
              buttonWidth="280px"
            />
            <MultiFilterDropdown
              inputTitle={translate(language, "GLOBAL.LANGUAGE")}
              tags={availableLanguages}
              onSelectTags={(tags) => {
                setParameters((parameters: any) => ({ ...parameters, languages: tags.map(String) }));
                setActiveCustomFilter("");
              }}
              defaultValue={parameters.languages}
              enableSelectAll
              considerEmptyOnAllSelected
              showSearchBar
              language={language}
              buttonWidth="280px"
            />
            <div className="white-background rounded p-2 s-bold">
              <Form.Check
                type="checkbox"
                id="unassigned-checkbox"
                label={translate(language, "PAGES.MEETINGS.FILTERS.UNASSIGNED_MEETINGS")}
                checked={parameters.showUnassigned}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setParameters((parameters: any) => ({ ...parameters, showUnassigned: e.target.checked }))
                }
              />
            </div>
          </div>

          <hr className="my-0" />

          <div className="d-flex flex-column gap-2">
            <div className="d-flex align-items-center justify-content-between">
              <span className="xs-regular dark-gray-font text-uppercase">
                {translate(language, "PAGES.MEETINGS.FILTERS.SAVED_FILTERS")}
              </span>
              <DstButton
                variant="light"
                btnClass="d-flex align-items-center"
                value={translate(language, "PAGES.MEETINGS.FILTERS.CREATE_CUSTOM_FILTER")}
                btnImageBefore={<span className="dst-icon-plus-circle me-1" />}
                clickFunction={() => setShowPopupCreation(true)}
                disabled={
                  !parameters.meetingTypes.length &&
                  !parameters.authors.length &&
                  !parameters.animators.length &&
                  !parameters.programs.length &&
                  !parameters.languages.length
                }
              />
            </div>
            <Dropdown show={showPopupSelection} onToggle={() => setShowPopupSelection(!showPopupSelection)}>
              <DstButton
                variant="light"
                btnImageBefore={<span className="dst-icon-settings-04 me-1" />}
                value={
                  translate(language, "PAGES.MEETINGS.FILTERS.CURRENT_FILTER_SET") +
                  (activeCustomFilter
                    ? ` ${activeCustomFilter}`
                    : ` ${translate(language, "PAGES.MEETINGS.FILTERS.DEFAULT_FILTER")}`)
                }
                clickFunction={() => setShowPopupSelection(!showPopupSelection)}
                btnClass="w-100 text-truncate"
                tooltip={activeCustomFilter}
                tooltipPosition="top"
              />
              <CustomFilterDropdown
                closeModal={() => setShowPopupSelection(false)}
                language={language}
                show={showPopupSelection}
                onApplyFilter={_handleApplyCustomFilter}
                activeCustomFilter={activeCustomFilter}
              />
            </Dropdown>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="p-3 border-top">
            <DstButton
              variant="secondary"
              btnClass="w-100"
              value={translate(language, "PAGES.MEETINGS.FILTERS.CLEAR_FILTERS")}
              clickFunction={_clearAllFilters}
            />
          </div>
        )}
      </div>

      <CreateCustomFilterPopup
        show={showPopupCreation}
        closeModal={() => setShowPopupCreation(false)}
        language={language}
        currentFilters={currentFilters}
      />
    </div>
  );
};

export default Meetings;
